import React, { useState, useEffect } from 'react';

import useMicrosoftAuth, {
    AuthenticationState,
    ErrorType as AuthErrorType,
} from 'PlugAndPlay/_global/jsx/integrations/microsoft/useMicrosoftAuth.jsx';

import EmailsListingContext, {
    EmailsListingContextErrorType as ErrorType,
} from './emails-listing-context';

export default function OutlookProvider({ clientId, mailboxLink, children }) {
    const scopes = ['Mail.Read'];
    const {
        authenticationState,
        retryAuthentication,
        errorType: authErrorType,
        getAccessToken,
        loginButton,
    } = useMicrosoftAuth({
        clientId,
        scopes,
    });

    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [emails, setEmails] = useState([]);

    // Convert useMicrosoftAuth error to PersonalDocumentListingContext error type
    useEffect(() => {
        if (authErrorType !== null) {
            if (authErrorType === AuthErrorType.AUTH_ERROR) {
                setError(ErrorType.AUTH_ERROR);
            } else if (authErrorType === AuthErrorType.SCOPE_ERROR) {
                setError(ErrorType.SCOPE_ERROR);
            } else if (authErrorType === AuthErrorType.POPUP_ERROR) {
                setError(ErrorType.POPUP_ERROR);
            } else {
                setError(ErrorType.ERROR);
            }
        }
    }, [authErrorType]);

    const getEmails = ({ count }) => {
        setIsLoading(true);
        getAccessToken().then((accessToken) => {
            const headers = new Headers();
            const bearer = `Bearer ${accessToken}`;
            headers.append('Authorization', bearer);
            headers.append('Content-Type', 'application/json');
            headers.append('Prefer', 'IdType="ImmutableId"');

            fetch(
                `https://graph.microsoft.com/v1.0/me/mailFolders/inbox/messages?$top=${count}&$select=id,isRead,bodyPreview,subject,webLink,from`,
                {
                    headers,
                    method: 'GET',
                }
            )
                .then((response) => response.json())
                .then((response) => {
                    const myEmails = response.value.map((myEmail) => {
                        const {
                            id,
                            isRead,
                            bodyPreview,
                            subject,
                            webLink,
                            from: {
                                emailAddress: { name },
                            },
                        } = myEmail;
                        return {
                            id,
                            from: name,
                            title: subject,
                            isRead: !isRead,
                            link: webLink,
                            subtitle: bodyPreview,
                        };
                    });
                    setEmails(myEmails);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                    // Set general error
                    setError(ErrorType.ERROR);
                });
        });
    };

    const restartAuthProcess = () => {
        setError(null);
        retryAuthentication();
    };

    return (
        <EmailsListingContext.Provider
            value={{
                isLoading:
                    authenticationState === AuthenticationState.NOT_STARTED
                        ? true
                        : isLoading,
                isReady:
                    authenticationState === AuthenticationState.AUTHENTICATED,
                isAuthNeeded:
                    authenticationState ===
                    AuthenticationState.NEEDS_LOGIN_CONSENT,
                signInLink: loginButton,
                error,
                emails,
                getEmails,
                restartAuthProcess,
                mailboxLink:
                    mailboxLink || 'https://outlook.office365.com/owa/',
            }}
        >
            {children}
        </EmailsListingContext.Provider>
    );
}
